import {
	ReactElement,
	useCallback,
	useContext
} from "react";
import classNames from "classnames";
import { DropOverContext } from "client/Components";
import { HubItemRowLayoutKind } from "client/Api/Hub/common";
import { localize } from "client_legacy/ndaw/culture";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { rowLayoutToNumberOfColumnsMap } from "../Row";
import styles from "./GridPicker.module.less";
import { MenuList } from "./MenuList";

export type GridPickerProps = {
	onSelect(kind: HubItemRowLayoutKind): void;
	tabbableIndex?: number;
};

export const GridPicker = (props: GridPickerProps): ReactElement => {
	const {
		onSelect,
		tabbableIndex
	} = props;

	const {
		onClose: closeContainingDropOver
	} = useContext(DropOverContext);

	const selectHandler = useCallback(
		(kind: HubItemRowLayoutKind): void => {
			onSelect(kind);
			closeContainingDropOver?.();
		},
		[closeContainingDropOver, onSelect]
	);

	return (
		<MenuList trackingId="grid-picker">
			<MenuList.Item
				image={<GridImage kind={HubItemRowLayoutKind.OneColumn} />}
				title={localize("_OneColumn")}
				onClick={() => selectHandler(HubItemRowLayoutKind.OneColumn)}
				trackingId="grid-one-column"
				index={0}
				tabbableIndex={tabbableIndex}
			/>
			<MenuList.Item
				image={<GridImage kind={HubItemRowLayoutKind.TwoColumn} />}
				title={localize("_TwoColumns")}
				onClick={() => selectHandler(HubItemRowLayoutKind.TwoColumn)}
				trackingId="grid-two-columns"
				index={1}
				tabbableIndex={tabbableIndex}
			/>
			<MenuList.Item
				image={<GridImage kind={HubItemRowLayoutKind.ThreeColumn} />}
				title={localize("_ThreeColumns")}
				onClick={() => selectHandler(HubItemRowLayoutKind.ThreeColumn)}
				trackingId="grid-three-columns"
				index={2}
				tabbableIndex={tabbableIndex}
			/>
			<MenuList.Item
				image={<GridImage kind={HubItemRowLayoutKind.FourColumn} />}
				title={localize("_FourColumns")}
				onClick={() => selectHandler(HubItemRowLayoutKind.FourColumn)}
				trackingId="grid-four-columns"
				index={3}
				tabbableIndex={tabbableIndex}
			/>
			<MenuList.Item
				image={<GridImage kind={HubItemRowLayoutKind.TwoColumnBigLeft} />}
				title={localize("_BigLeftColumn")}
				onClick={() => selectHandler(HubItemRowLayoutKind.TwoColumnBigLeft)}
				trackingId="grid-big-left-column"
				index={4}
				tabbableIndex={tabbableIndex}
			/>
			<MenuList.Item
				image={<GridImage kind={HubItemRowLayoutKind.TwoColumnBigRight} />}
				title={localize("_BigRightColumn")}
				onClick={() => selectHandler(HubItemRowLayoutKind.TwoColumnBigRight)}
				trackingId="grid-big-right-column"
				index={5}
				tabbableIndex={tabbableIndex}
			/>
		</MenuList>
	);
};
GridPicker.displayName = "GridPicker";

export type GridImageProps = {
	kind: HubItemRowLayoutKind;
};

export const GridImage = (props: GridImageProps): ReactElement => {
	const {
		kind
	} = props;

	return (
		<div
			className={classNames(
				styles.image,
				classNameForRowLayoutKindMap[kind]
			)}
		>
			{range(rowLayoutToNumberOfColumnsMap[kind]).map(
				i => (
					<div key={i} className={styles.column}></div>
				)
			)}
		</div>
	);
};
GridImage.displayName = "GridImage";

const classNameForRowLayoutKindMap: {[key in HubItemRowLayoutKind]: string | undefined } = {
	[HubItemRowLayoutKind.OneColumn]: undefined,
	[HubItemRowLayoutKind.TwoColumn]: undefined,
	[HubItemRowLayoutKind.ThreeColumn]: undefined,
	[HubItemRowLayoutKind.FourColumn]: undefined,
	[HubItemRowLayoutKind.TwoColumnBigLeft]: styles.image_twoColumnBigLeft,
	[HubItemRowLayoutKind.TwoColumnBigRight]: styles.image_twoColumnBigRight
};
